// The rename slot of the context slot (§12.0, R1818): the four cards that
// stand in the place of the rename card, one at a time. They are the
// rename card of a hull (monetization.md §3.2, R1250), the name card of a
// course plot (R2589), the naming sheet of a seamount (v2 §4.3, R2626) and
// the cache sheet with its SEARCH picker (v2 §9.3, §9.4, R2649, R2699).
//
// Each card is built when it opens, and again after a pick, because the
// selection card is rewritten whenever its markup changes, and a rewrite
// would take the box from under the finger that types in it. The card that
// opens takes the place from the card that stood (R2993). `ui/overlay.ts`
// hosts the element, offers the finds at each render and routes the
// presses here.

import {
  FLEET_NAME_MAX_RUNES, FLEET_NAME_MIN_RUNES, KEY_FLEET_DESIGNER, KEY_FREE_TEXT,
  SEAMOUNT_NAME_MAX_RUNES, SEAMOUNT_NAME_MIN_RUNES,
} from '../../gen/balance';
import { Mission } from '../../gen/proto/aquaphobia';
import type { CacheRow, NamedSeamount, Wreck } from '../../gen/proto/aquaphobia';
import { card as CS, overlay as S } from '../../gen/strings';
import type { GameState } from '../../state';
import { cellAt, mpxToPx } from '../../world';
import {
  CACHE_FIND_OPEN, CACHE_NOTE_OPEN, CACHE_OPEN, CACHE_PHRASE, CACHE_SAVE, CACHE_SEARCH, CACHE_SEARCH_HERE,
  cacheIntent, cacheSheet, fitSea, freshNotes, isPicker, noteRefusal, pickerPoint, pickerRows, pickerSub,
  type CacheDraft, type PickerRow, type SearchFleet,
} from '../caches';
import { type CardEnv, type CardSpec, cardHtml } from '../cards/card';
import { markSvg } from '../cards/marks';
import { workFit } from '../cards/orders';
import { POI_SEARCH } from '../cards/treasure';
import { keyboardInset } from '../chatdock';
import type { ContextSlot } from '../ctxslot';
import { esc } from '../esc';
import { cssColor } from '../format';
import { isSheet } from '../layout';
import {
  SM_FIRST, SM_SAVE, SM_SECOND, freshFinds, namingCard, seamountIntent, startWords, type NamingDraft,
} from '../seamountname';
import { toChromePx } from '../uiscale';
import { el } from './markup';
import { TREASURE_SEARCH } from './treasure';
import type { UIActions, UIFleet } from './types';

/** Writes how far the keyboard of a telephone reaches over the foot of the
 *  screen as `--kb-b` on `host`, in chrome px, while `typing` (card 05 b of
 *  round 087); takes it off otherwise. The chat band reads it too. */
export function writeKbInset(host: HTMLElement, typing: boolean): void {
  const kb = typing ? toChromePx(keyboardInset(window.innerHeight, window.visualViewport ?? null)) : 0;
  if (kb > 0) host.style.setProperty('--kb-b', `${Math.round(kb)}px`);
  else host.style.removeProperty('--kb-b');
}

/** The key of the rows of an open picker: their fleets, their order and
 *  their reasons (R2722). */
function pickerKey(rows: readonly PickerRow[]): string {
  return rows.map((r) => `${r.f.id}:${r.why}`).join(',');
}

/** What the desk reads of the overlay. */
export interface NameDeskHost {
  readonly state: GameState;
  readonly actions: UIActions;
  readonly slot: ContextSlot;
  /** The own fleets at sea (`Overlay.fleets`). */
  fleets(): UIFleet[];
  cardEnv(): CardEnv;
  /** An error notice: a SAVE the rule refuses. */
  refuse(text: string): void;
  /** The ⚑ of a cache note (`ReportDesk.cacheNoteMark`). */
  cacheNoteMark(c: CacheRow): string;
}

export class NameDesk {
  /** The one element of the four cards, in the rename place of the slot. */
  readonly el = el('div', '', 'renamecard');
  /** The hull the rename card names, or 0 while it is not up. */
  private renameFor = 0;
  private unbindNameInput: (() => void) | null = null;
  /** The SAVE of the name card of a course plot, or null (R2589). */
  private nameSave: ((name: string) => void) | null = null;
  /** The naming sheet of a seamount while it stands, or null (R2626). */
  private naming: NamingDraft | null = null;
  /** The cache sheet while it stands, or null (v2 §9.3, R2649). */
  private cacheDraft: CacheDraft | null = null;
  /** The rows of the open picker, as fleet ids and reasons, for the 4 Hz
   *  pass that writes its distances again (R2722). */
  private pickerKey = '';
  /** The finds whose note this page offered, or held at its first Empire
   *  frame (R2649). */
  private readonly cacheSeen = new Set<number>();
  /** The find list of the last Empire frame the desk read, or null
   *  before the first one. */
  private cacheFinds: readonly CacheRow[] | null = null;
  /** The seamount finds this page has offered once (R2626). */
  private readonly smOffered = new Set<number>();
  /** The find list of the last Empire frame the desk read. */
  private smFinds: readonly NamedSeamount[] | null = null;

  constructor(private readonly host: NameDeskHost) {
    this.el.hidden = true;
  }

  /** The hull the rename card names, or 0: the card goes when the
   *  selection stops showing that hull. */
  get hull(): number { return this.renameFor; }

  /** A card stands in the place. */
  get shown(): boolean { return !this.el.hidden; }

  /** The own fleet on the map with this id, if any. */
  private seaFleet(id: number): UIFleet | undefined {
    return id ? this.host.fleets().find((f) => f.id === id) : undefined;
  }

  /** A card of the four stands, so no find is offered over it. */
  private busy(): boolean {
    return this.naming !== null || this.renameFor !== 0 || this.nameSave !== null || this.cacheDraft !== null;
  }

  /**
   * Takes the place for one card (R2993): the state of every other card
   * goes, the input of the card that stood is unbound, and the element
   * stands. The caller sets the state of its own card, then writes it.
   */
  private take(): void {
    this.unbindNameInput?.();
    this.unbindNameInput = null;
    this.renameFor = 0;
    this.nameSave = null;
    this.naming = null;
    this.cacheDraft = null;
    this.el.hidden = false;
  }

  /** Writes one card into the element, keeps the typed text and binds the
   *  input of the new box. */
  private write(spec: CardSpec, save: () => void, value: string, focus = false): void {
    this.unbindNameInput?.();
    this.unbindNameInput = null;
    this.el.innerHTML = cardHtml(spec, this.host.cardEnv());
    this.bindNameInput(value, save, focus);
  }

  /** The text in the box of the card that stands, or ''. */
  private typed(): string {
    return this.el.querySelector<HTMLInputElement>('.renin')?.value ?? '';
  }

  /**
   * One press of a card in the place, or of a row that opens one (R1250,
   * R2626, R2649, R2699, R2860). It answers false for any other act.
   */
  act(act: string, id: number, n: number): boolean {
    const a = this.host.actions;
    switch (act) {
      // The rename card (R1250). A second press on RENAME closes it.
      case 'rename':
        if (this.renameFor === id) this.close(); else this.openRename(id);
        return true;
      case 'renamesave': this.saveRename(); return true;
      case 'renamecancel': this.close(); return true;
      // The naming sheet of a seamount (R2626): a word of each list, NAME.
      case SM_FIRST: if (this.naming) { this.naming.first = id; this.drawNaming(); } return true;
      case SM_SECOND: if (this.naming) { this.naming.second = id; this.drawNaming(); } return true;
      case SM_SAVE: this.saveSeamountName(); return true;
      // The cache sheet (v2 §9.3, R2649): CACHES of ⋯, NOTE of a row, the
      // phrase, and LEAVE CACHE or SAVE NOTE.
      case CACHE_OPEN: this.toggleCacheSheet(id); return true;
      case CACHE_NOTE_OPEN:
        this.openCacheSheet({ mode: 'note', cacheId: id, phrase: 0, freeText: this.host.state.hasFeature(KEY_FREE_TEXT) });
        return true;
      case CACHE_FIND_OPEN:
        this.cacheSeen.add(id);
        this.openCacheSheet({ mode: 'find', cacheId: id });
        return true;
      case CACHE_PHRASE:
        if (this.cacheDraft?.mode === 'leave' || this.cacheDraft?.mode === 'note') {
          this.cacheDraft.phrase = id;
          this.drawCacheSheet();
        }
        return true;
      case CACHE_SAVE: this.saveCacheSheet(); return true;
      // The SEARCH picker and SEARCH HERE of the leave sheet (R2699): the
      // order goes out, and the sheet goes. A picker at a wreck point
      // sends the SEARCH there (R2860).
      case CACHE_SEARCH: {
        const d = this.cacheDraft;
        if (d && isPicker(d) && d.at) {
          const [x, y] = pickerPoint(d);
          a.searchPoint(id, x, y);
        } else {
          a.searchAt(id, n);
        }
        this.close();
        return true;
      }
      case CACHE_SEARCH_HERE: a.setMission(id, Mission.MISSION_SEARCH); this.close(); return true;
      // SEARCH on the card of a selected point of interest (R3138): the
      // SEARCH picker of the cell of the point.
      case POI_SEARCH: this.openSearchPicker(n); return true;
      // SEARCH on a row of WRECKS WITH CARGO (v2 §9.6, R2834, R2860): the
      // SEARCH picker at the point of that wreck.
      case TREASURE_SEARCH: {
        const w = this.host.state.wrecks.find((x) => x.fleet === id);
        if (w) this.openWreckPicker(w);
        return true;
      }
    }
    return false;
  }

  /**
   * Opens the rename card for one own hull. The box holds the name part:
   * the hull name without its designator prefix (R44), which the server
   * keeps. On a telephone the card is the raised context sheet (round 083
   * card 16, round 088 card 06), and the keyboard lifts it.
   */
  openRename(fleetId: number): void {
    const f = this.seaFleet(fleetId);
    const s = this.host.state;
    if (!f || !s.hasFeature(KEY_FLEET_DESIGNER)) return;
    const cut = f.name.indexOf(' ');
    const prefix = cut > 0 ? f.name.slice(0, cut) : '';
    const part = cut > 0 ? f.name.slice(cut + 1) : f.name;
    this.take();
    this.renameFor = fleetId;
    this.openNameCard(markSvg('fleet', cssColor(s.faction)), S.rename_head({ name: f.name }), S.btn_rename_title,
      S.rename_hint({ prefix, min: FLEET_NAME_MIN_RUNES, max: FLEET_NAME_MAX_RUNES }), part);
  }

  /**
   * The name card of a course plot (R2589, round 088 card 09): SAVE AS
   * PLOT of the list of legs, and RENAME of a row of FLEET · ROUTES. It is
   * the rename card of a hull with its own head and its own SAVE; the
   * server holds the name to the rule of a hull name part (R2587).
   */
  openPlotName(head: string, value: string, save: (name: string) => void): void {
    this.take();
    this.nameSave = save;
    this.openNameCard(`<span class="ck-mk cs-route" aria-hidden="true">${esc(CS.plot_mark)}</span>`, head, esc(head),
      CS.plot_name_hint({ min: FLEET_NAME_MIN_RUNES, max: FLEET_NAME_MAX_RUNES }), value);
  }

  /** The card of one name box, SAVE and CANCEL: the rename card of a hull
   *  and the name card of a course plot. `label` is escaped markup. */
  private openNameCard(mark: string, title: string, label: string, hint: string, value: string): void {
    // The claim stands the card before the box takes the focus.
    this.host.slot.claim('rename', true);
    this.write({
      size: 'card', mark, title, ink: { faction: this.host.state.faction },
      body: '<div class="ck-sect">'
        + `<input class="renin" type="text" autocomplete="off" maxlength="${FLEET_NAME_MAX_RUNES * 2}" `
        + `aria-label="${label}"></div>`,
      note: { lines: [esc(hint)] },
      actions: [
        { act: 'renamesave', label: S.btn_rename_save, face: 'dark' },
        { act: 'renamecancel', label: S.btn_rename_cancel, face: 'quiet' },
      ],
    }, () => this.saveRename(), value, true);
  }

  /** Binds the input of each name card. Replacement and close remove the
   *  viewport listener even when removing the focused input sends no blur. */
  private bindNameInput(value: string, save: () => void, focus = false): void {
    const box = this.el.querySelector<HTMLInputElement>('.renin');
    if (!box) return;
    box.value = value;
    const keydown = (e: KeyboardEvent) => {
      e.stopPropagation(); // typing must never reach the map's key handler
      if (e.key === 'Enter') save();
      else if (e.key === 'Escape') this.close();
    };
    const vv = window.visualViewport ?? null;
    const fit = () => this.fitKeyboard();
    const onFocus = () => { vv?.addEventListener('resize', fit); fit(); };
    const onBlur = () => { vv?.removeEventListener('resize', fit); fit(); };
    box.addEventListener('keydown', keydown);
    box.addEventListener('focus', onFocus);
    box.addEventListener('blur', onBlur);
    this.unbindNameInput = () => {
      vv?.removeEventListener('resize', fit);
      box.removeEventListener('keydown', keydown);
      box.removeEventListener('focus', onFocus);
      box.removeEventListener('blur', onBlur);
      this.el.style.removeProperty('--kb-b');
    };
    if (focus) { box.focus(); box.select(); }
  }

  /** Stands the card on the keyboard of a telephone, or back in the slot. */
  private fitKeyboard(): void {
    const box = this.el.querySelector('.renin');
    writeKbInset(this.el, isSheet() && box !== null && document.activeElement === box);
  }

  /** SAVE: sends the name part and puts the card away. The server answers
   *  a refusal with a notice, and the next frame names the hull. */
  saveRename(): void {
    const name = this.typed().trim();
    const save = this.nameSave;
    if (save && name !== '') save(name);
    else if (this.renameFor !== 0 && name !== '') this.host.actions.renameFleet(this.renameFor, name);
    this.close();
  }

  /**
   * The 4 Hz pass (R2626, R2649, R2722): offers the first new seamount
   * find and the first new cache note, and makes an open picker follow
   * its fleets.
   */
  sync(): void {
    this.offerSeamount();
    this.offerCacheNote();
    this.refreshPicker();
  }

  /**
   * Offers the naming sheet of a seamount (v2 §4.3, round 088 card 06,
   * R2626): the first find of the Empire frame the page has not offered,
   * while no other name card stands. LATER puts it away; the find stays on
   * the list until the commander names it.
   */
  private offerSeamount(): void {
    const finds = this.host.state.empire?.seamountFinds;
    if (!finds || finds === this.smFinds) return;
    this.smFinds = finds;
    // A find that left the list carries a name now: its sheet goes.
    const open = this.naming;
    if (open && !finds.some((f) => f.body === open.find.body)) this.close();
    if (this.busy()) return;
    const next = freshFinds(finds, this.smOffered)[0];
    if (!next) return;
    this.smOffered.add(next.body);
    this.openSeamountName(next);
  }

  /**
   * Offers the find card of a cache (v2 §9.4, R2649): the first find of the
   * Empire frame with a note that the page has not offered, while no other
   * card stands in the place. The finds of the first frame count as
   * offered, so a reload offers nothing again; READ of YOUR FINDS opens a
   * card again.
   */
  private offerCacheNote(): void {
    const finds = this.host.state.empire?.cacheFinds;
    if (!finds || finds === this.cacheFinds) return;
    const first = this.cacheFinds === null;
    this.cacheFinds = finds;
    if (first) {
      for (const c of finds) this.cacheSeen.add(c.id);
      return;
    }
    if (this.busy()) return;
    const next = freshNotes(finds, this.cacheSeen)[0];
    if (!next) return;
    this.cacheSeen.add(next.id);
    this.openCacheSheet({ mode: 'find', cacheId: next.id });
  }

  /**
   * NAME on the peek of an own find (R2633): opens the naming sheet of the
   * find again, after LATER too. A find that left the Empire frame carries
   * a name now, and the press does nothing.
   */
  openSeamountFind(body: number): void {
    const find = this.host.state.empire?.seamountFinds.find((f) => f.body === body);
    if (!find) return;
    this.smOffered.add(body);
    this.openSeamountName(find);
  }

  /** Opens the naming sheet of one find in the place. */
  private openSeamountName(find: NamedSeamount): void {
    this.take();
    this.naming = { find, ...startWords(find.body), freeText: this.host.state.hasFeature(KEY_FREE_TEXT) };
    this.drawNaming();
    this.host.slot.claim('rename', true);
  }

  /** Writes the naming sheet again after a pick, and keeps the typed text. */
  private drawNaming(): void {
    const d = this.naming;
    if (d) this.write(namingCard(d), () => this.saveSeamountName(), this.typed());
  }

  /** NAME: sends the name and puts the sheet away. A free text of the
   *  wrong length stays on the sheet with the rule as a notice. */
  private saveSeamountName(): void {
    const d = this.naming;
    if (!d) return;
    const intent = seamountIntent(d, this.typed());
    if (!intent) {
      this.host.refuse(CS.seamount_bad_length({ min: SEAMOUNT_NAME_MIN_RUNES, max: SEAMOUNT_NAME_MAX_RUNES }));
      return;
    }
    this.host.actions.nameSeamount(intent);
    this.close();
  }

  /**
   * Opens the cache sheet (v2 §9.3, §9.4, round 088 card 10, R2649) in the
   * place: LEAVE A CACHE for one fleet, or the note of one own cache. It
   * is built once, and again after a pick of a phrase.
   */
  private openCacheSheet(d: CacheDraft): void {
    this.take();
    this.cacheDraft = d;
    this.drawCacheSheet();
    this.host.slot.claim('rename', true);
  }

  /** SEARCH on the peek of water with no own fleet selected (R2699): the
   *  SEARCH picker of the cell, while `treasure` holds. `at` is the point
   *  of a wreck with cargo (R2860): the fleet searches there. */
  openSearchPicker(cell: number, at?: readonly [number, number]): void {
    if (!this.host.state.hasFeatureFlag('treasure')) return;
    this.openCacheSheet(at ? { mode: 'search', cell, at } : { mode: 'search', cell });
  }

  /** SEARCH on a row of WRECKS WITH CARGO (R2860): the SEARCH picker at
   *  the point of the wreck. */
  private openWreckPicker(w: Wreck): void {
    const x = mpxToPx(w.xMpx);
    const y = mpxToPx(w.yMpx);
    this.openSearchPicker(cellAt(x, y), [x, y]);
  }

  /** The own fleets at sea, as a picker reads them. */
  private pickerSea(): SearchFleet[] {
    const s = this.host.state;
    // A combat ship or a Dredger alone (v2 §9.6, R2834).
    return fitSea(s, this.host.fleets().map((f) => ({
      id: f.id, name: f.name, kind: s.ship(f.cls)?.name ?? '', cls: f.cls, x: f.x, y: f.y, anchor: f.anchor,
    })));
  }

  /**
   * The 4 Hz pass of an open picker (R2699 Open, R2722): the distances
   * follow the fleets. While the rows keep their fleets, their order and
   * their reasons, the pass writes the small line of each row in place and
   * keeps the buttons under the pointer; any other change draws the sheet
   * again.
   */
  private refreshPicker(): void {
    const d = this.cacheDraft;
    if (!d || !isPicker(d)) return;
    const rows = pickerRows(d, this.pickerSea());
    const subs = this.el.querySelectorAll<HTMLElement>('.ck-rn i');
    if (pickerKey(rows) !== this.pickerKey || subs.length !== rows.length) {
      this.drawCacheSheet();
      return;
    }
    rows.forEach((r, i) => {
      const t = pickerSub(r);
      if (subs[i].textContent !== t) subs[i].textContent = t;
    });
  }

  /** CACHES of the ⋯ of an own fleet card: the leave sheet of that fleet,
   *  or no sheet after a second press. */
  private toggleCacheSheet(fleetId: number): void {
    const d = this.cacheDraft;
    if (d?.mode === 'leave' && d.fleetId === fleetId) { this.close(); return; }
    const f = this.seaFleet(fleetId);
    const s = this.host.state;
    if (!f || !s.hasFeatureFlag('treasure')) return;
    this.openCacheSheet({
      mode: 'leave', fleetId, fleetName: f.name, phrase: 0, freeText: s.hasFeature(KEY_FREE_TEXT),
      fit: workFit(s, f.cls),
    });
  }

  /** Writes the cache sheet again, and keeps the typed text. A note state
   *  whose cache left the list closes the sheet. */
  private drawCacheSheet(): void {
    const d = this.cacheDraft;
    if (!d) return;
    const sea = isPicker(d) ? this.pickerSea() : [];
    const spec = cacheSheet(this.host.state, d, (c) => this.host.cacheNoteMark(c), sea);
    if (!spec) { this.close(); return; }
    this.pickerKey = isPicker(d) ? pickerKey(pickerRows(d, sea)) : '';
    this.write(spec, () => this.saveCacheSheet(), this.typed());
  }

  /** LEAVE CACHE or SAVE NOTE: sends the intent and puts the sheet away.
   *  A free text of the wrong length and a note with no words stay on the
   *  sheet with the rule as a notice. */
  private saveCacheSheet(): void {
    const d = this.cacheDraft;
    if (!d) return;
    const text = this.typed();
    const why = noteRefusal(d, text);
    if (why) { this.host.refuse(why); return; }
    const a = cacheIntent(d, text);
    if (!a) { this.host.refuse(CS.cache_note_pick); return; }
    this.host.actions.cacheAction(a);
    this.close();
  }

  /** Takes the card of the place away. */
  close(): void {
    this.unbindNameInput?.();
    this.unbindNameInput = null;
    this.renameFor = 0;
    this.nameSave = null;
    this.naming = null;
    this.cacheDraft = null;
    this.host.slot.claim('rename', false);
    this.el.hidden = true;
    this.el.innerHTML = '';
    this.el.style.removeProperty('--kb-b');
  }

  /** Takes the input listeners off, for the overlay's own teardown. */
  destroy(): void {
    this.unbindNameInput?.();
  }
}
